import { useEffect, useState } from "react";
import { Mail, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function SmtpSettings() {
  const t = useT();
  const [form, setForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_email: "",
    use_tls: true,
    use_ssl: false,
  });
  const [hasPassword, setHasPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/smtp");
      setForm((f) => ({
        ...f,
        host: data.host || "",
        port: data.port || 587,
        username: data.username || "",
        from_email: data.from_email || "",
        use_tls: !!data.use_tls,
        use_ssl: !!data.use_ssl,
      }));
      setHasPassword(!!data.has_password);
    } catch (err) {
      // No SMTP saved yet (404) is expected on first visit; surface other errors.
      if (err?.response?.status && err.response.status !== 404) {
        console.warn("smtp load failed:", err.message);
      }
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.host) return toast.error("Host required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!form.password) delete payload.password;
      await api.put("/smtp", payload);
      toast.success("SMTP saved");
      setForm((f) => ({ ...f, password: "" }));
      setHasPassword(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testEmail) return toast.error("Recipient required");
    try {
      await api.post("/smtp/test", { to: testEmail });
      toast.success("Test email sent");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="smtp-page">
      <div>
        <p className="kpi-label">{t("nav.smtp")}</p>
        <h1 className="text-2xl font-bold tracking-tight">{t("smtp.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("smtp.subtitle")}</p>
      </div>

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4" /> {t("smtp.server_settings")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label htmlFor="sh">{t("ds.host")}</Label>
              <Input id="sh" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="smtp.example.com" data-testid="smtp-host" />
            </div>
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label htmlFor="sp">{t("ds.port")}</Label>
              <Input id="sp" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 587 })} data-testid="smtp-port" />
            </div>
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label htmlFor="su">{t("ds.username")}</Label>
              <Input id="su" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="smtp-user" />
            </div>
            <div className="col-span-2 sm:col-span-1 space-y-1.5">
              <Label htmlFor="spw">{t("common.password")}{hasPassword && ` ${t("ds.password_keep")}`}</Label>
              <Input id="spw" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="smtp-pass" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="sf">{t("smtp.from_email")}</Label>
              <Input id="sf" type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="alerts@example.com" data-testid="smtp-from" />
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">{t("smtp.starttls")}</p>
                <p className="text-xs text-muted-foreground">{t("smtp.starttls_hint")}</p>
              </div>
              <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v })} data-testid="smtp-tls" />
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-medium">{t("smtp.implicit_ssl")}</p>
                <p className="text-xs text-muted-foreground">{t("smtp.implicit_ssl_hint")}</p>
              </div>
              <Switch checked={form.use_ssl} onCheckedChange={(v) => setForm({ ...form, use_ssl: v })} data-testid="smtp-ssl" />
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="smtp-save">
            {saving ? t("common.loading") : t("common.save_settings")}
          </Button>
        </CardContent>
      </Card>

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Send className="h-4 w-4" /> {t("smtp.send_test_title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="you@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="smtp-test-recipient"
            />
            <Button variant="outline" onClick={sendTest} data-testid="smtp-test-send">
              {t("smtp.send_test")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
